// 金額表示

import type { ZonedDateTimeString } from "@sa-nu/shared-utils/date";
import {
  getCurrentTime,
  PlainDate,
  ZonedDateTime,
} from "@sa-nu/shared-utils/date";

import type {
  AccommodationReservation,
  AccommodationReservationProvider,
  AccommodationReservationProviderInput,
  Site,
} from "./__generated__/gql/graphql";
import { AccommodationReservationProviderType } from "./__generated__/gql/graphql";

// TODO: 一般的なやり方があるかもなので調べる
type Resource = {
  __typename?: "Area";
  slug: string;
};
export function pagePath(resource: Resource): string {
  switch (resource.__typename) {
    case "Area":
      return `/areas/${resource.slug}`;
    default:
      // eslint-disable-next-line no-console
      console.error("resource.__typename is not defined");
      return "";
  }
}

export function buildAccommodationReservationProviderInput(
  provider: AccommodationReservationProvider,
): AccommodationReservationProviderInput {
  switch (provider.__typename) {
    case "StandardPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.StandardPlan,
        providerUserSubscriptionId: provider.userSubscriptionId,
      };
    case "CoOwnersPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.CoOwnersPlan,
        providerUserOwnershipId: provider.userOwnershipId,
      };
    case "FullOwnerPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.FullOwnerPlan,
        providerUserOwnershipId: provider.userOwnershipId,
      };
    case "BusinessPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.BusinessPlan,
        providerOrganizationId: provider.organizationId,
      };
    case "OneMoreCabinAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.OneMoreCabin,
        providerOneMoreCabinParentAccommodationReservationId:
          provider.parentAccommodationReservationId,
      };
    case "StayTicketAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.StayTicket,
        providerStayTicketUnitId: provider.stayTicketUnitId,
      };
    case "StayDirectAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.StayDirect,
      };
    case "SanuIncAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.SanuInc,
        providerSanuIncUsage: provider.usage,
        providerSanuIncStaffGroupId: provider.staffGroup?.id,
      };
    case "OnlineTravelAgentAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.OnlineTravelAgent,
        providerOtaIsIkyuDiamondMember: provider.isIkyuDiamondMember,
        providerOtaLeadAccommodationReservationId:
          provider.leadAccommodationReservationId,
      };
    case "TrialPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.TrialPlan,
        providerTrialContractId: provider.trialContractId,
      };
    case "StaffPlanAccommodationReservationProvider":
      return {
        providerType: AccommodationReservationProviderType.StaffPlan,
        providerStaffContractId: provider.staffContractId,
      };
    case undefined:
      throw new Error("provider is undefined");
  }
}

export function getAccommodationName({
  provider,
  reservedFrom,
  accommodation,
}: {
  provider: { __typename?: string };
  reservedFrom: ZonedDateTimeString;
  accommodation: {
    name: string;
    site: { name: string };
  };
}) {
  const accommodationName = accommodation.name;
  const siteName = accommodation.site.name;

  if (
    provider.__typename === "OnlineTravelAgentAccommodationReservationProvider"
  ) {
    const now = getCurrentTime();
    const twoDaysBeforeCheckIn = ZonedDateTime.from(reservedFrom).subtract({
      days: 2,
    });
    const isNowEarlierThanTwoDaysBeforeCheckIn =
      ZonedDateTime.compare(now, twoDaysBeforeCheckIn) < 0;

    return isNowEarlierThanTwoDaysBeforeCheckIn ? siteName : accommodationName;
  }
  return accommodationName;
}

// 2026/3/1 までの処理
// その時期を過ぎたら単に `accommodation.site.isRemote` を見るだけでOK
// https://github.com/sa-nu/2h/issues/3518
export function shoudShowRemoteCancelPolicy(
  accommodation: {
    site: Pick<Site, "isRemote" | "slug">;
  },
  reservation: Pick<AccommodationReservation, "lastNight">,
): boolean {
  if (!accommodation.site.isRemote) return false;

  if (accommodation.site.slug === "mon") {
    // 奄美MONは3/1以降に遠方リモートポリシーが適用される
    const startNewPolicyDate = new PlainDate(2026, 3, 1);
    return PlainDate.compare(startNewPolicyDate, reservation.lastNight) <= 0;
  } else {
    return true;
  }
}
